import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useRootStore } from './RootStore';

// User types
export type UserRole = 'admin' | 'proctor' | 'testTaker' | 'faculty';

// Authentication states
export type AuthState = 'unauthenticated' | 'mfa_required' | 'authenticated';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatar?: string;
  mfaEnabled?: boolean;
}

interface AuthStoreState {
  authState: AuthState;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  tempCredentials: { email: string; tempToken: string } | null;
  isAuthenticated: () => boolean;
  isMfaRequired: () => boolean;
  setAuthState: (state: AuthState) => void;
  setIsLoading: (value: boolean) => void;
  setUser: (user: User | null) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  verifyMfa: (code: string) => Promise<boolean>;
  logout: () => void;
}

const useAuthStore = create<AuthStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        authState: 'unauthenticated',
        isLoading: false,
        user: null,
        error: null,
        tempCredentials: null,

        isAuthenticated: () => get().authState === 'authenticated',
        isMfaRequired: () => get().authState === 'mfa_required',

        setAuthState: (state) => set({ authState: state }),
        setIsLoading: (value) => set({ isLoading: value }),
        setUser: (user) => set({ user }),
        setError: (error) => set({ error }),

        login: async (email: string, password: string) => {
          try {
            get().setIsLoading(true);
            get().setError(null);

            // Simulate API call
            await new Promise((resolve) => setTimeout(resolve, 1000));


            // Simple validation
            if (!password || password.length < 6) {
              throw new Error('Invalid password');
            }


            // Mock user data
            const mockUser: User = {
              id: '1',
              email: email,
              firstName: 'John',
              lastName: 'Doe',
              role: 'admin',
              mfaEnabled: true,
            };

            // Check if MFA is required
            if (mockUser.mfaEnabled) {
              set({
                tempCredentials: {
                  email: email,
                  tempToken: 'temp-token-for-mfa',
                },
                authState: 'mfa_required',
              });
              return true;
            }


            // Complete login if MFA not required
            set({
              user: mockUser,
              authState: 'authenticated',
              tempCredentials: null,
            });
            return true;
          } catch (error) {
            get().setError(error instanceof Error ? error.message : 'Failed to login');
            return false;
          } finally {
            get().setIsLoading(false);
          }
        },

        verifyMfa: async (code: string) => {
          try {
            get().setIsLoading(true);
            get().setError(null);


            // Simulate API verification
            await new Promise((resolve) => setTimeout(resolve, 1000));


            // Simple validation
            if (!code || code.length !== 6) {
              throw new Error('Invalid verification code');
            }


            // Get the temporary credentials
            const { email } = get().tempCredentials || {};
            if (!email) {
              throw new Error('No pending authentication');
            }


            // Mock user data
            const mockUser: User = {
              id: '1',
              email,
              firstName: 'John',
              lastName: 'Doe',
              role: 'admin',
              mfaEnabled: true,
            };

            // Complete login
            set({
              user: mockUser,
              authState: 'authenticated',
              tempCredentials: null,
            });
            return true;
          } catch (error) {
            get().setError(
              error instanceof Error ? error.message : 'Failed to verify code'
            );
            return false;
          } finally {
            get().setIsLoading(false);
          }
        },

        logout: () => {
          set({
            authState: 'unauthenticated',
            user: null,
            tempCredentials: null,
            error: null,
          });
        },
      }),
      {
        name: 'auth-storage', // name of the item in the storage (must be unique)
        partialize: (state) => ({
          user: state.user,
          authState: state.authState,
        }),
      }
    ),
    {
      name: 'auth-store', // name of the slice of store (for devtools)
    }
  )
);

// Register the store with the root store
const initializeStore = () => {
  // Register this store with the root store
  useRootStore.getState().setAuthStore(useAuthStore);
};

// Initialize the store when this module is imported
initializeStore();

export default useAuthStore;
export type { AuthStoreState };

